/* Result screens: the single-product report and the side-by-side comparison. */

import { capitalise, escapeHtml, html, qs } from "./dom";
import { NOVA_SHORT, breakdownList, gauge, listWords, novaColour, packArt, pill, scaleBar, sigaBox, summarySentence } from "./grade";
import { contributeDraft, contributeProduct, describeFailure, lookupAlternatives } from "./api";
import { icon } from "./icons";
import type { AdditiveFlag, Alternatives, Analysis, Comparison, ContributeDraft, Impact, ProductSummary, Safety } from "./types";

/** How a product is labelled on screen. */
export interface Display {
  name: string;
  brand: string;
  /** Uploaded front photo (object URL) or Open Food Facts' pack photo. */
  image: string;
}

export function displayFor(product: ProductSummary, typedName: string, fallback: string, image = ""): Display {
  return {
    name: typedName || product.name || fallback,
    brand: product.brand || "",
    image: image || product.image_front_url || "",
  };
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

function sourceLine(product: ProductSummary): string {
  return product.source === "openfoodfacts" ? "Open Food Facts" : "Label photo (OCR)";
}

/* ======================================================================
   Single product
   ====================================================================== */

function heroCard(d: Display, a: Analysis): string {
  const s = a.safety;
  const p = a.product;
  const pack = d.image
    ? `<div class="pack-photo"><img src="${escapeHtml(d.image)}" alt="${escapeHtml(d.name)} pack"></div>`
    : packArt(d.name, d.brand, p.barcode || d.name);
  const meta = [sourceLine(p), p.barcode ? "EAN " + p.barcode : ""].filter(Boolean);
  return `
<section class="card hero-card" style="--tone:${novaColour(s.nova_group)}">
  <div class="hero-top">
    ${pack}
    <div class="hero-id">
      ${d.brand ? `<span class="eyebrow">${escapeHtml(d.brand)}</span>` : ""}
      <h2 class="serif">${escapeHtml(d.name)}</h2>
      <p class="muted">${escapeHtml(meta.join(" · "))}</p>
      ${p.url ? `<a class="link small" href="${escapeHtml(p.url)}" target="_blank" rel="noopener">View on Open Food Facts ${icon("external")}</a>` : ""}
    </div>
  </div>
  <div class="score-box">
    ${gauge(s.nova_group)}
    <div class="score-text">
      ${pill(s)}
      <p class="score-summary">${escapeHtml(summarySentence(s))}</p>
      <p class="muted small">${plural(s.ins_count, "INS code")} · ${plural(s.flags.length, "concern")} · ${plural(a.ingredients.length, "ingredient")} found</p>
    </div>
  </div>
  ${scaleBar(s.nova_group)}
  ${sigaBox(s)}
</section>`;
}

function positivesCard(s: Safety): string {
  if (!s.positives.length) return "";
  const rows = s.positives
    .map(
      (p) =>
        `<li><span class="ok-ic">${icon("tick")}</span><span>${escapeHtml(p.label)}</span>` +
        (p.claimed ? `<span class="tag">on pack</span>` : "") +
        `</li>`,
    )
    .join("");
  return `<section class="card list-card"><h3>${icon("check")} Positives</h3><ul class="good-list">${rows}</ul></section>`;
}

/** Red pushes the product into NOVA 4, amber into NOVA 3; grey marks no group. */
function flagTone(f: AdditiveFlag): string {
  return f.nova === 4 ? "var(--g-e)" : f.nova === 3 ? "var(--g-c)" : "var(--ink-3)";
}

function flagRow(f: AdditiveFlag): string {
  return (
    `<li><span class="sev" style="--tone:${flagTone(f)}"></span>` +
    `<div><strong>${escapeHtml(f.label)}</strong><small>${escapeHtml(f.note)}</small></div></li>`
  );
}

function negativesCard(s: Safety): string {
  if (!s.flags.length) {
    return `<section class="card list-card"><h3 class="warn-h">${icon("warn")} Negatives</h3>
      <p class="muted">Nothing to flag. The label declares no additives we look for.</p></section>`;
  }
  return `<section class="card list-card"><h3 class="warn-h">${icon("warn")} Negatives</h3>
    <ul class="bad-list">${s.flags.map(flagRow).join("")}</ul></section>`;
}

function methodCard(a: Analysis): string {
  const s = a.safety;
  const rows: [string, string][] = [
    ["Ingredients from", sourceLine(a.product)],
    ["NOVA group", `${s.nova_group} · ${s.nova_name}`],
    ["Group decided by", s.nova_source],
  ];
  if (s.nova_source === "Open Food Facts" && s.computed_nova_group !== s.nova_group) {
    rows.push(["Our own analysis", `NOVA ${s.computed_nova_group} (${NOVA_SHORT[s.computed_nova_group]})`]);
  }
  if (s.nova_reasons.length) rows.push(["Because of", [...new Set(s.nova_reasons)].join(", ")]);
  if (s.siga_class) {
    rows.push(["Siga class", `${s.siga_class} · ${s.siga_name ?? ""}`]);
    rows.push(["Siga score", `${s.siga_estimated ? "about " : ""}${s.siga_score}/100 (CleanScore's own reading of the class)`]);
    if (s.grade) rows.push(["Grade", s.grade]);
  }
  if (a.used_front) rows.push(["Front of pack", "Read for claims"]);
  return `<section class="card method">
    <h3>${icon("analyse")} How this was classified</h3>
    <dl>${rows.map(([k, v]) => `<div><dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd></div>`).join("")}</dl>
    ${breakdownList(s)}
    <p class="caveat">${escapeHtml(s.caveat)}</p>
    ${s.siga_caveat ? `<p class="caveat">${escapeHtml(s.siga_caveat)}</p>` : ""}
  </section>`;
}

function ingredientsTab(a: Analysis): string {
  const byName = new Map<string, Impact>(a.impact.map((i) => [i.ingredient, i]));
  const items = a.ingredients.length
    ? `<ul class="ing-list">${a.ingredients
        .map((name) => {
          const info = byName.get(name);
          const extra = info?.benefit || info?.highlight || "";
          return (
            `<li><span class="ing-dot"></span><div><strong>${escapeHtml(capitalise(name))}</strong>` +
            (info && info.category !== "-" ? `<span class="tag">${escapeHtml(info.category)}</span>` : "") +
            (extra ? `<small>${escapeHtml(extra)}</small>` : "") +
            `</div></li>`
          );
        })
        .join("")}</ul>`
    : `<p class="muted">No ingredient list was found in the label text.</p>`;
  const raw = a.ocr.ingredients.trim();
  return `<section class="card list-card">
      <h3>${icon("leaf")} Ingredients <span class="count">${a.ingredients.length}</span></h3>${items}
    </section>
    ${raw ? `<details class="card raw"><summary>Label text as read</summary><pre>${escapeHtml(raw)}</pre></details>` : ""}`;
}

function additivesTab(s: Safety): string {
  if (!s.flags.length) {
    return `<section class="card empty">${icon("shield", "ic big")}<h3>No additives declared</h3>
      <p class="muted">We found no colours, flavour enhancers, preservatives or fillers on this label.</p></section>`;
  }
  return s.flags
    .map(
      (f) => `<section class="card additive" style="--tone:${flagTone(f)}">
        <div class="additive-head"><strong>${escapeHtml(f.label)}</strong>
          ${f.nova ? `<span class="pill small" style="--tone:${flagTone(f)}">NOVA ${f.nova}</span>` : ""}</div>
        ${f.ins ? `<span class="mono muted small">INS ${escapeHtml(f.ins)}</span>` : ""}
        <p>${escapeHtml(f.note)}</p>
        ${f.matched ? `<p class="muted small">On the label: “${escapeHtml(f.matched)}”</p>` : ""}
      </section>`,
    )
    .join("");
}

/** Tabs: Summary / Ingredients / Additives. */
function tabs(a: Analysis): HTMLElement {
  const panels: Record<string, string> = {
    summary: positivesCard(a.safety) + negativesCard(a.safety) + methodCard(a),
    ingredients: ingredientsTab(a),
    additives: additivesTab(a.safety),
  };
  const box = html(`<div class="tabs-wrap">
    <div class="tabs" role="tablist">
      <button role="tab" data-tab="summary" aria-selected="true">Summary</button>
      <button role="tab" data-tab="ingredients" aria-selected="false">Ingredients</button>
      <button role="tab" data-tab="additives" aria-selected="false">Additives
        ${a.safety.flags.length ? `<span class="count">${a.safety.flags.length}</span>` : ""}</button>
    </div>
    <div class="tab-panel" role="tabpanel"></div>
  </div>`);
  const panel = qs(box, ".tab-panel");
  const show = (key: string): void => {
    panel.innerHTML = panels[key] ?? "";
    box.querySelectorAll<HTMLButtonElement>("[role=tab]").forEach((b) => {
      b.setAttribute("aria-selected", String(b.dataset.tab === key));
    });
  };
  box.querySelectorAll<HTMLButtonElement>("[role=tab]").forEach((b) => {
    b.addEventListener("click", () => show(b.dataset.tab ?? "summary"));
  });
  show("summary");
  return box;
}

export function renderAnalysis(d: Display, a: Analysis): HTMLElement {
  const root = html(`<div class="report"></div>`);
  root.innerHTML = heroCard(d, a);
  root.appendChild(tabs(a));
  const slot = alternativesSlot(a);
  if (slot) root.appendChild(slot);
  const offer = contributeSlot(a);
  if (offer) root.appendChild(offer);
  return root;
}

/* ---- Add to Open Food Facts ----------------------------------------------
   A product read from label photos is only in our own catalogue. This card
   offers to add it to Open Food Facts: the user checks and corrects what the
   photos gave, agrees to publish, and only then is anything sent. */

/** The nutrition panel as the form shows it, by Open Food Facts nutriment name. */
const CONTRIBUTE_NUTRIENTS: [string, string][] = [
  ["energy-kcal", "Energy (kcal)"], ["proteins", "Protein"], ["carbohydrates", "Carbohydrate"],
  ["sugars", "Sugars"], ["added-sugars", "Added sugars"], ["fat", "Fat"],
  ["saturated-fat", "Saturated fat"], ["trans-fat", "Trans fat"], ["cholesterol", "Cholesterol"],
  ["fiber", "Fibre"], ["salt", "Salt"], ["sodium", "Sodium"],
];

function contributeSlot(a: Analysis): HTMLElement | null {
  const barcode = a.product.barcode;
  if (!barcode) return null;
  const slot = html(`<section class="card contribute" hidden></section>`);
  contributeDraft(barcode)
    .then((draft) => {
      if (!draft.eligible) {
        // Already there (sent from here or added by someone else): link to it.
        if (!draft.url) return;
        slot.hidden = false;
        slot.innerHTML = `<h3>${icon("check")} On Open Food Facts</h3>
          <p class="muted">This product is in the Open Food Facts database, where everyone can find it.</p>
          <a class="link" href="${escapeHtml(draft.url)}" target="_blank" rel="noopener">View on Open Food Facts ${icon("external")}</a>`;
        return;
      }
      slot.hidden = false;
      slot.innerHTML = contributeIntro(draft);
      slot.querySelector<HTMLButtonElement>(".contribute-start")?.addEventListener("click", () => {
        slot.innerHTML = contributeForm(draft);
        wireContributeForm(slot, draft);
      });
    })
    .catch((error) => console.warn("contribute:", describeFailure(error)));
  return slot;
}

function serverNote(draft: ContributeDraft): string {
  return draft.test_server
    ? `<span class="tag">Test server</span> Entries go to Open Food Facts' test site, not the public database.`
    : `This adds the product to the public Open Food Facts database. It can't be withdrawn later.`;
}

function contributeIntro(draft: ContributeDraft): string {
  const action = draft.configured
    ? `<button class="btn primary contribute-start" type="button">${icon("upload")} Add to Open Food Facts</button>`
    : `<p class="muted small">Not set up yet: the server needs OFF_USER_ID and OFF_PASSWORD in its .env.</p>`;
  return `<h3>${icon("upload")} Not on Open Food Facts yet</h3>
    <p class="muted">This product was read from your label photos, so only CleanScore knows it.
      Adding it to Open Food Facts lets everyone find it, and gives it an official NOVA group.</p>
    <p class="muted small">${serverNote(draft)}</p>
    ${action}`;
}

function contributeForm(draft: ContributeDraft): string {
  const text = (name: string, label: string, value: string | undefined, hint = "") =>
    `<label class="field-label" for="cf-${name}">${label}${hint ? ` <em>${hint}</em>` : ""}</label>
     <div class="field"><input id="cf-${name}" name="${name}" value="${escapeHtml(value ?? "")}"></div>`;
  const amount = ([name, label]: [string, string]) =>
    `<label class="cf-gram"><span>${label}</span>
       <input type="number" step="0.001" min="0" name="n:${name}" value="${draft.nutrients?.[name] ?? ""}"
              placeholder="${name === "energy-kcal" ? "kcal" : "g"}"></label>`;
  const photos = (draft.photos ?? [])
    .map((k) => `<label class="cf-check"><input type="checkbox" name="photo" value="${k}" checked> ${capitalise(k)} photo</label>`)
    .join("");
  return `<h3>${icon("upload")} Add to Open Food Facts</h3>
    <p class="muted small">Check every field: this is what will be published. ${serverNote(draft)}</p>
    <form class="cf-form" novalidate>
      ${text("product_name", "Product name", draft.product_name)}
      ${text("brands", "Brand", draft.brands)}
      ${text("quantity", "Quantity", draft.quantity, "e.g. 100 g")}
      ${text("categories", "Category", "", "optional, e.g. Spice blends")}
      <label class="field-label" for="cf-ingredients">Ingredients <em>as printed, in English</em></label>
      <textarea id="cf-ingredients" name="ingredients_text" rows="5">${escapeHtml(draft.ingredients_text ?? "")}</textarea>
      <span class="field-label">Nutrition per 100 g <em>optional; energy in kcal, the rest in grams</em></span>
      <div class="cf-grams">${CONTRIBUTE_NUTRIENTS.map(amount).join("")}</div>
      ${photos ? `<span class="field-label">Photos to send</span><div class="cf-photos">${photos}</div>` : ""}
      <label class="cf-check cf-consent"><input type="checkbox" name="consent">
        <span>These photos were taken by us, and I agree to publish this product on Open Food Facts
        (data under ODbL, photos under CC-BY-SA).</span></label>
      <p class="cf-message" role="status"></p>
      <button class="btn primary wide cf-send" type="submit">Send to Open Food Facts</button>
    </form>`;
}

function wireContributeForm(slot: HTMLElement, draft: ContributeDraft): void {
  const form = qs<HTMLFormElement>(slot, ".cf-form");
  const send = qs<HTMLButtonElement>(slot, ".cf-send");
  const message = qs(slot, ".cf-message");
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const value = (k: string) => String(data.get(k) ?? "").trim();
    if (!data.get("consent")) {
      message.textContent = "Tick the box to confirm the photos are yours and may be published.";
      return;
    }
    const nutrients: Record<string, string> = {};
    for (const [name] of CONTRIBUTE_NUTRIENTS) {
      const given = value("n:" + name);
      if (given) nutrients[name] = given;
    }
    send.disabled = true;
    send.classList.add("busy");
    message.textContent = "";
    contributeProduct(draft.barcode, {
      product_name: value("product_name"),
      brands: value("brands"),
      quantity: value("quantity"),
      categories: value("categories"),
      ingredients_text: value("ingredients_text"),
      nutrients,
      photos: data.getAll("photo").map(String),
      consent: true,
    })
      .then((result) => {
        if (!result.ok) {
          message.textContent = result.errors.join(" ");
          send.disabled = false;
          return;
        }
        const notes = result.errors.length ? `<p class="muted small">${escapeHtml(result.errors.join(" "))}</p>` : "";
        const sent = result.photos.length
          ? `Sent with the ${escapeHtml(result.photos.join(", "))} photo${result.photos.length > 1 ? "s" : ""}.`
          : "";
        slot.innerHTML = `<h3>${icon("check")} Added to Open Food Facts</h3>
          <p class="muted">Thank you. ${sent} Open Food Facts will work out its NOVA group from the ingredients.</p>
          ${notes}
          <a class="link" href="${escapeHtml(result.url)}" target="_blank" rel="noopener">View it on Open Food Facts ${icon("external")}</a>`;
      })
      .catch((error) => {
        message.textContent = describeFailure(error);
        send.disabled = false;
      })
      .finally(() => send.classList.remove("busy"));
  });
}

/** Better-processed products from the same category. Filled in after the page
 *  shows, because it costs a second Open Food Facts lookup; only products that
 *  came from a barcode can have one. */
function alternativesSlot(a: Analysis): HTMLElement | null {
  const barcode = a.product.barcode;
  if (!barcode || a.product.source !== "openfoodfacts") return null;
  const slot = html(`<section class="card alternatives" hidden></section>`);
  lookupAlternatives(barcode)
    .then((alt) => {
      if (alt.status === "unavailable") return; // no card beats a card saying nothing useful
      slot.hidden = false;
      slot.innerHTML = alt.status === "ok" ? alternativesCard(alt) : `<h3>${icon("analyse")} Better alternatives</h3>
        <p class="muted">${alt.status === "none" ? "Nothing less processed was found in this category." : "This product has no category in Open Food Facts, so alternatives can't be suggested."}</p>`;
    })
    .catch((error) => console.warn("alternatives:", describeFailure(error)));
  return slot;
}

function alternativesCard(alt: Alternatives): string {
  const rows = alt.items
    .map((i) => {
      const img = i.image_front_url ? `<img src="${escapeHtml(i.image_front_url)}" alt="" loading="lazy">` : packArt(i.name, i.brand, i.barcode);
      return `<li><a href="${escapeHtml(i.url)}" target="_blank" rel="noopener">${img}
        <span><strong>${escapeHtml(i.name || "Unnamed product")}</strong>
        <small>${escapeHtml(i.brand)}</small></span>
        <span class="pill small" style="--tone:${novaColour(i.nova_group)}">NOVA ${i.nova_group}</span></a></li>`;
    })
    .join("");
  return `<h3>${icon("analyse")} Better alternatives</h3>
    <p class="muted small">Less processed products in ${escapeHtml(alt.category)}, from Open Food Facts.</p>
    <ul class="alt-list">${rows}</ul>`;
}

/* ======================================================================
   Comparison
   ====================================================================== */

/** The comparison seen from one side, shaped like a single analysis. */
export function sideAnalysis(c: Comparison, side: "khetika" | "competitor"): Analysis {
  return {
    ingredients: c[side],
    impact: [],
    safety: side === "khetika" ? c.khetika_safety : c.competitor_safety,
    product: c.products[side],
    ocr: {
      ingredients: side === "khetika" ? c.ocr.khetika : c.ocr.competitor,
      front: side === "khetika" ? c.ocr.khetika_front : c.ocr.competitor_front,
    },
    used_front: c.used_front[side],
  };
}

/** The same comparison with the two sides exchanged. */
export function swapComparison(c: Comparison): Comparison {
  return {
    khetika: c.competitor,
    competitor: c.khetika,
    shared: c.shared,
    khetika_unique: c.competitor_unique,
    competitor_unique: c.khetika_unique,
    khetika_safety: c.competitor_safety,
    competitor_safety: c.khetika_safety,
    additive_diff: { only_khetika: c.additive_diff.only_competitor, only_competitor: c.additive_diff.only_khetika },
    products: { khetika: c.products.competitor, competitor: c.products.khetika },
    ocr: {
      khetika: c.ocr.competitor,
      competitor: c.ocr.khetika,
      khetika_front: c.ocr.competitor_front,
      competitor_front: c.ocr.khetika_front,
    },
    used_front: { khetika: c.used_front.competitor, competitor: c.used_front.khetika },
  };
}

/** 0 = first is cleaner, 1 = second, null = level. Lower NOVA wins, then fewer flags. */
function winner(a: Safety, b: Safety): 0 | 1 | null {
  if (a.nova_group !== b.nova_group) return a.nova_group < b.nova_group ? 0 : 1;
  if (a.flags.length !== b.flags.length) return a.flags.length < b.flags.length ? 0 : 1;
  return null;
}

function miniCard(d: Display, s: Safety, tag: string, best: boolean): string {
  return `<div class="vs-card ${best ? "best" : ""}">
    ${best ? `<span class="crown">${icon("crown")} Cleaner pick</span>` : ""}
    ${gauge(s.nova_group, "sm")}
    ${pill(s)}
    <span class="eyebrow">${escapeHtml(d.brand || tag)}</span>
    <strong class="vs-name">${escapeHtml(d.name)}</strong>
  </div>`;
}

function yesNo(present: boolean): string {
  return present
    ? `<span class="yn bad">${icon("cross")} Yes</span>`
    : `<span class="yn good">${icon("tick")} No</span>`;
}

/** Bold the better value in a numeric row (lower is better). */
function numCells(a: number, b: number): string {
  const cls = (x: number, y: number) => (x < y ? "better" : "");
  return `<td class="num ${cls(a, b)}">${a}</td><td class="num ${cls(b, a)}">${b}</td>`;
}

/** Siga class and score rows; the score is higher-is-better. Skipped for old history entries. */
function sigaRows(a: Safety, b: Safety): string {
  if (!a.siga_class || !b.siga_class || a.siga_score === undefined || b.siga_score === undefined) return "";
  const mark = (s: Safety) => `${s.siga_class}${s.siga_estimated ? "*" : ""}`;
  const cls = (x: number, y: number) => (x > y ? "better" : "");
  return (
    `<tr><th scope="row">Siga class</th><td class="num">${escapeHtml(mark(a))}</td><td class="num">${escapeHtml(mark(b))}</td></tr>` +
    `<tr><th scope="row">Siga score (0-100)</th>` +
    `<td class="num ${cls(a.siga_score, b.siga_score)}">${a.siga_score}</td>` +
    `<td class="num ${cls(b.siga_score, a.siga_score)}">${b.siga_score}</td></tr>`
  );
}

function compareTable(c: Comparison, da: Display, db: Display): string {
  const a = c.khetika_safety;
  const b = c.competitor_safety;
  const checkRows = a.checks
    .map((check, i) => {
      const other = b.checks[i];
      return `<tr><th scope="row">${escapeHtml(check.label)}</th><td>${yesNo(check.present)}</td><td>${yesNo(other?.present ?? false)}</td></tr>`;
    })
    .join("");
  return `<section class="card table-card">
    <table class="cmp">
      <thead><tr><th>Parameter</th><th>${escapeHtml(da.brand || da.name)}</th><th>${escapeHtml(db.brand || db.name)}</th></tr></thead>
      <tbody>
        <tr><th scope="row">NOVA group</th>${numCells(a.nova_group, b.nova_group)}</tr>
        ${sigaRows(a, b)}
        ${checkRows}
        <tr><th scope="row">INS codes</th>${numCells(a.ins_count, b.ins_count)}</tr>
        <tr><th scope="row">Additives flagged</th>${numCells(a.flags.length, b.flags.length)}</tr>
        <tr><th scope="row">Ingredients found</th>
          <td class="num ${c.khetika.length > c.competitor.length ? "better" : ""}">${c.khetika.length}</td>
          <td class="num ${c.competitor.length > c.khetika.length ? "better" : ""}">${c.competitor.length}</td></tr>
      </tbody>
    </table>
  </section>`;
}

function labels(flags: AdditiveFlag[]): string {
  return listWords(flags.map((f) => f.label));
}

function whyBanner(c: Comparison, da: Display, db: Display, win: 0 | 1 | null): string {
  const a = c.khetika_safety;
  const b = c.competitor_safety;
  if (win === null) {
    const same = a.flags.length
      ? `Both are NOVA ${a.nova_group} with ${plural(a.flags.length, "flagged additive")} each.`
      : `Both are NOVA ${a.nova_group} and neither label declares an additive we flag.`;
    return `<section class="why tie"><span class="eyebrow">Why</span><h3 class="serif">Evenly matched.</h3><p>${escapeHtml(same)}</p></section>`;
  }
  const [W, L] = win === 0 ? [da, db] : [db, da];
  const [ws, ls] = win === 0 ? [a, b] : [b, a];
  const onlyLoser = win === 0 ? c.additive_diff.only_competitor : c.additive_diff.only_khetika;
  const onlyWinner = win === 0 ? c.additive_diff.only_khetika : c.additive_diff.only_competitor;
  const head =
    ws.nova_group !== ls.nova_group
      ? `${W.brand || W.name} is less processed.`
      : `${W.brand || W.name} has fewer additives.`;
  const parts = [
    `${W.name} is NOVA ${ws.nova_group} (${NOVA_SHORT[ws.nova_group].toLowerCase()}); ` +
      `${L.name} is NOVA ${ls.nova_group} (${NOVA_SHORT[ls.nova_group].toLowerCase()}).`,
  ];
  if (onlyLoser.length) parts.push(`${L.brand || L.name} declares ${labels(onlyLoser)}, which ${W.brand || W.name} does not.`);
  if (onlyWinner.length) parts.push(`${W.brand || W.name} still declares ${labels(onlyWinner)}.`);
  return `<section class="why"><span class="eyebrow">Why</span><h3 class="serif">${escapeHtml(head)}</h3>
    <p>${escapeHtml(parts.join(" "))}</p></section>`;
}

function overlapCard(c: Comparison, da: Display, db: Display): string {
  const chips = (list: string[]) =>
    list.length
      ? list.map((i) => `<span class="chip">${escapeHtml(capitalise(i))}</span>`).join("")
      : `<span class="muted small">None</span>`;
  return `<section class="card list-card">
    <h3>${icon("leaf")} Ingredients side by side</h3>
    <div class="overlap">
      <div><span class="eyebrow">In both</span><div class="chips">${chips(c.shared)}</div></div>
      <div><span class="eyebrow">Only ${escapeHtml(da.brand || da.name)}</span><div class="chips">${chips(c.khetika_unique)}</div></div>
      <div><span class="eyebrow">Only ${escapeHtml(db.brand || db.name)}</span><div class="chips">${chips(c.competitor_unique)}</div></div>
    </div>
  </section>`;
}

export function renderComparison(c: Comparison, da: Display, db: Display): HTMLElement {
  const win = winner(c.khetika_safety, c.competitor_safety);
  const root = html(`<div class="report compare-report">
    <div class="vs-row">
      ${miniCard(da, c.khetika_safety, "Product A", win === 0)}
      <span class="vs">vs</span>
      ${miniCard(db, c.competitor_safety, "Product B", win === 1)}
    </div>
    ${compareTable(c, da, db)}
    ${whyBanner(c, da, db, win)}
    ${overlapCard(c, da, db)}
    <h2 class="section-title serif">Full reports</h2>
    <div class="seg" role="tablist">
      <button role="tab" data-side="khetika" aria-selected="true">${escapeHtml(da.name)}</button>
      <button role="tab" data-side="competitor" aria-selected="false">${escapeHtml(db.name)}</button>
    </div>
    <div class="side-report"></div>
  </div>`);
  const slot = qs(root, ".side-report");
  const show = (side: "khetika" | "competitor"): void => {
    slot.replaceChildren(renderAnalysis(side === "khetika" ? da : db, sideAnalysis(c, side)));
    root.querySelectorAll<HTMLButtonElement>(".seg [role=tab]").forEach((b) => {
      b.setAttribute("aria-selected", String(b.dataset.side === side));
    });
  };
  root.querySelectorAll<HTMLButtonElement>(".seg [role=tab]").forEach((b) => {
    b.addEventListener("click", () => show(b.dataset.side === "competitor" ? "competitor" : "khetika"));
  });
  show("khetika");
  return root;
}
